import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Calendar } from '../components/ui/calendar';
import { doctors } from '../data/mockData';
import { todayAppointments as fallbackAppointments, recentPatients as fallbackPatients } from '../data/appointmentsData';
import {
  Search, Calendar as CalendarIcon, Clock, User, FileText,
  MessageSquare, ChevronRight, Stethoscope, X, Save, CheckCircle2, Loader2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const allDoctors = [
  ...doctors.generalCare,
  ...doctors.lifestyle,
  ...doctors.specialists
];

const DashboardPage = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [appointments, setAppointments] = useState([]);
  const [patients, setPatients] = useState([]);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [notes, setNotes] = useState('');
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [loading, setLoading] = useState(true);

  // Fetch appointments and patients from backend
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [apptRes, patRes] = await Promise.all([
          fetch(`${API}/appointments`),
          fetch(`${API}/patients`)
        ]);
        const apptData = await apptRes.json();
        const patData = await patRes.json();

        const appts = apptData.appointments || [];
        const pats = patData.patients || [];

        // Map backend fields to frontend format
        setAppointments(appts.length > 0 ? appts.map(a => ({
          id: a.id,
          patientName: a.patient_name,
          time: a.time,
          doctor: a.doctor,
          doctorSpecialty: a.doctor_specialty,
          type: a.type,
          status: a.status,
          notes: a.notes,
          symptoms: a.symptoms,
          date: a.date
        })) : fallbackAppointments);

        setPatients(pats.length > 0 ? pats : fallbackPatients);
      } catch (err) {
        console.error('Failed to fetch dashboard data:', err);
        setAppointments(fallbackAppointments);
        setPatients(fallbackPatients);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Search
  useEffect(() => {
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const doctorResults = allDoctors.filter(
        d => d.name.toLowerCase().includes(q) || d.specialty.toLowerCase().includes(q)
      ).map(d => ({ ...d, type: 'doctor' }));

      const patientResults = patients.filter(
        p => p.name.toLowerCase().includes(q) || (p.condition || '').toLowerCase().includes(q)
      ).map(p => ({ ...p, type: 'patient' }));

      const appointmentResults = appointments.filter(
        a => a.patientName.toLowerCase().includes(q) || a.doctor.toLowerCase().includes(q) ||
          a.type.toLowerCase().includes(q) || a.symptoms.toLowerCase().includes(q)
      ).map(a => ({ ...a, type: 'appointment' }));

      setSearchResults([...doctorResults, ...patientResults, ...appointmentResults]);
      setShowSearchResults(true);
    } else {
      setSearchResults([]);
      setShowSearchResults(false);
    }
  }, [searchQuery, appointments, patients]);

  const handleSearchSelect = (item) => {
    if (item.type === 'doctor') {
      navigate(`/chat/${encodeURIComponent(item.name)}`);
    } else if (item.type === 'patient') {
      const pa = appointments.find(a => a.patientName === item.name);
      if (pa) openNotesModal(pa);
    } else if (item.type === 'appointment') {
      openNotesModal(item);
    }
    setSearchQuery('');
    setShowSearchResults(false);
  };

  const openNotesModal = (appointment) => {
    setSelectedAppointment(appointment);
    setNotes(appointment.notes || '');
    setShowNotesModal(true);
    setSaveSuccess(false);
  };

  const saveNotes = async () => {
    try {
      await fetch(`${API}/appointments/${selectedAppointment.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notes })
      });
      setAppointments(prev =>
        prev.map(a => a.id === selectedAppointment.id ? { ...a, notes } : a)
      );
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2000);
    } catch (err) {
      console.error('Failed to save notes:', err);
      // Optimistic local update anyway
      setAppointments(prev =>
        prev.map(a => a.id === selectedAppointment.id ? { ...a, notes } : a)
      );
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2000);
    }
  };

  const startConsultation = (appointment) => {
    sessionStorage.setItem('pendingPatientInfo', JSON.stringify({
      patientName: appointment.patientName,
      symptoms: appointment.symptoms,
      appointmentType: appointment.type,
      appointmentId: appointment.id
    }));
    navigate(`/chat/${encodeURIComponent(appointment.doctor)}`);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'in-progress': return 'bg-green-100 text-green-700 border-green-200';
      case 'completed': return 'bg-gray-100 text-gray-600 border-gray-200';
      default: return 'bg-blue-100 text-blue-700 border-blue-200';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2" data-testid="dashboard-title">Dashboard</h1>
          <p className="text-gray-600">Manage appointments and patient consultations</p>
        </div>

        {/* Search Bar */}
        <div className="relative mb-8">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input type="text" placeholder="Search patients, doctors, symptoms, or appointments..."
              value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-4 text-lg rounded-xl border-gray-200 focus:border-[#166534] focus:ring-[#166534]"
              data-testid="dashboard-search-input" />
            {searchQuery && (
              <button onClick={() => { setSearchQuery(''); setShowSearchResults(false); }}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            )}
          </div>

          {showSearchResults && searchResults.length > 0 && (
            <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-lg border border-gray-100 max-h-96 overflow-y-auto z-50" data-testid="search-results-dropdown">
              {searchResults.map((item, index) => (
                <button key={`${item.type}-${item.id || index}`} onClick={() => handleSearchSelect(item)}
                  className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-50 border-b border-gray-50 last:border-0 text-left">
                  {item.type === 'doctor' && (
                    <>
                      <div className="w-10 h-10 rounded-full bg-[#166534] flex items-center justify-center">
                        <Stethoscope className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{item.name}</p>
                        <p className="text-sm text-gray-500">{item.specialty}</p>
                      </div>
                      <span className="ml-auto text-xs bg-green-100 text-green-700 px-2 py-1 rounded">Doctor</span>
                    </>
                  )}
                  {item.type === 'patient' && (
                    <>
                      <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center">
                        <User className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{item.name}</p>
                        <p className="text-sm text-gray-500">{item.condition} &bull; {item.doctor}</p>
                      </div>
                      <span className="ml-auto text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">Patient</span>
                    </>
                  )}
                  {item.type === 'appointment' && (
                    <>
                      <div className="w-10 h-10 rounded-full bg-purple-500 flex items-center justify-center">
                        <CalendarIcon className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{item.patientName} - {item.time}</p>
                        <p className="text-sm text-gray-500">{item.doctor} &bull; {item.symptoms}</p>
                      </div>
                      <span className="ml-auto text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">Appointment</span>
                    </>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-10 h-10 animate-spin text-[#166534]" />
          </div>
        ) : (
          <>
            <div className="grid lg:grid-cols-3 gap-8">
              {/* Calendar */}
              <div className="lg:col-span-1">
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                  <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <CalendarIcon className="w-5 h-5 text-[#166534]" /> Schedule
                  </h2>
                  <Calendar mode="single" selected={selectedDate} onSelect={setSelectedDate} className="rounded-md border-0" />
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <p className="text-sm text-gray-500">Selected Date</p>
                    <p className="font-semibold text-gray-900">
                      {selectedDate?.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                    </p>
                    <p className="text-sm text-[#166534] mt-1">{appointments.length} appointments today</p>
                  </div>
                </div>
              </div>

              {/* Appointments */}
              <div className="lg:col-span-2">
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                  <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <Clock className="w-5 h-5 text-[#166534]" /> Today's Appointments
                  </h2>
                  <div className="space-y-3" data-testid="appointments-list">
                    {appointments.map((appointment) => (
                      <div key={appointment.id}
                        className="flex items-center gap-4 p-4 rounded-xl border border-gray-100 hover:border-[#166534] hover:shadow-md transition-all group"
                        data-testid={`appointment-${appointment.id}`}>
                        <div className="flex-shrink-0 text-center">
                          <p className="text-lg font-bold text-gray-900">{appointment.time.split(' ')[0]}</p>
                          <p className="text-xs text-gray-500">{appointment.time.split(' ')[1]}</p>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-semibold text-gray-900 truncate">{appointment.patientName}</p>
                            <span className={`text-xs px-2 py-0.5 rounded-full border ${getStatusColor(appointment.status)}`}>{appointment.status}</span>
                          </div>
                          <p className="text-sm text-gray-500 truncate">{appointment.doctor}</p>
                          <p className="text-xs text-gray-400 truncate">{appointment.symptoms}</p>
                        </div>
                        <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Button variant="outline" size="sm" onClick={() => openNotesModal(appointment)}
                            className="border-gray-200 hover:border-[#166534] hover:text-[#166534]" data-testid={`notes-btn-${appointment.id}`}>
                            <FileText className="w-4 h-4" />
                          </Button>
                          <Button size="sm" onClick={() => startConsultation(appointment)}
                            className="bg-[#166534] hover:bg-[#14532d] text-white" data-testid={`consult-btn-${appointment.id}`}>
                            <MessageSquare className="w-4 h-4 mr-1" /> Consult
                          </Button>
                        </div>
                        <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-[#166534] transition-colors" />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
              <div className="bg-white rounded-xl p-6 border border-gray-100">
                <p className="text-3xl font-bold text-[#166534]" data-testid="stat-total-appointments">{appointments.length}</p>
                <p className="text-sm text-gray-500">Today's Appointments</p>
              </div>
              <div className="bg-white rounded-xl p-6 border border-gray-100">
                <p className="text-3xl font-bold text-blue-600">{appointments.filter(a => a.status === 'in-progress').length}</p>
                <p className="text-sm text-gray-500">In Progress</p>
              </div>
              <div className="bg-white rounded-xl p-6 border border-gray-100">
                <p className="text-3xl font-bold text-purple-600">{patients.length}</p>
                <p className="text-sm text-gray-500">Recent Patients</p>
              </div>
              <div className="bg-white rounded-xl p-6 border border-gray-100">
                <p className="text-3xl font-bold text-orange-600">9</p>
                <p className="text-sm text-gray-500">AI Doctors Available</p>
              </div>
            </div>
          </>
        )}
      </main>

      {/* Notes Modal */}
      {showNotesModal && selectedAppointment && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="notes-modal">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-900">Patient Notes</h3>
                <button onClick={() => setShowNotesModal(false)} className="p-2 hover:bg-gray-100 rounded-full" data-testid="close-notes-modal">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="p-6 space-y-6 overflow-y-auto max-h-[60vh]">
              <div className="bg-gray-50 rounded-xl p-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Patient Name</p>
                    <p className="font-semibold text-gray-900">{selectedAppointment.patientName}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Appointment Time</p>
                    <p className="font-semibold text-gray-900">{selectedAppointment.time}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Doctor</p>
                    <p className="font-semibold text-gray-900">{selectedAppointment.doctor}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Type</p>
                    <p className="font-semibold text-gray-900">{selectedAppointment.type}</p>
                  </div>
                </div>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Presenting Symptoms</p>
                <p className="text-gray-600 bg-blue-50 rounded-lg p-3">{selectedAppointment.symptoms}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Treatment Notes</p>
                <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add consultation notes, treatment plan, recommendations..."
                  className="w-full h-40 p-4 border border-gray-200 rounded-xl focus:border-[#166534] focus:ring-1 focus:ring-[#166534] resize-none"
                  data-testid="notes-textarea" />
              </div>
            </div>
            <div className="p-6 border-t border-gray-100 flex items-center justify-between">
              {saveSuccess ? (
                <div className="flex items-center gap-2 text-green-600">
                  <CheckCircle2 className="w-5 h-5" /><span>Notes saved!</span>
                </div>
              ) : <div />}
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setShowNotesModal(false)}>Close</Button>
                <Button onClick={saveNotes} className="bg-[#166534] hover:bg-[#14532d] text-white" data-testid="save-notes-btn">
                  <Save className="w-4 h-4 mr-2" /> Save Notes
                </Button>
                <Button onClick={() => { setShowNotesModal(false); startConsultation(selectedAppointment); }}
                  className="bg-blue-600 hover:bg-blue-700 text-white">
                  <MessageSquare className="w-4 h-4 mr-2" /> Start Consultation
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default DashboardPage;
